'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X } from 'lucide-react';
import { geistMonoFont } from '@/_style/fonts';
import { Button } from '@/_components/UIComponents/Button';

const STORAGE_KEY = 'blockpedia-cookie-consent';

export default function CookieConsent() {
	const [accepted, setAccepted] = useState<boolean | null>(null);

	useEffect(() => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			setAccepted(stored === 'true');
		} catch {
			setAccepted(false);
		}
	}, []);

	const handleAccept = () => {
		setAccepted(true);
		try {
			localStorage.setItem(STORAGE_KEY, 'true');
		} catch {
			// storage unavailable
		}
	};

	// Don't render anything until we've checked localStorage
	if (accepted === null || accepted) return null;

	return (
		<div className='fixed right-4 bottom-4 left-4 z-50 mx-auto max-w-sm sm:right-6 sm:left-auto'>
			<div className='bg-card border-border relative overflow-hidden rounded-xl border px-5 pt-5 pb-4 shadow-xl'>
				{/* Subtle gradient accent */}
				<div className='pointer-events-none absolute top-0 right-0 left-0 h-px bg-gradient-to-r from-transparent via-pink-500/40 to-transparent' />

				{/* Dismiss */}
				<button
					onClick={handleAccept}
					className='text-muted-foreground hover:text-foreground absolute top-3 right-3 transition-colors'
					aria-label='Dismiss cookie notice'
				>
					<X className='h-4 w-4' />
				</button>

				{/* Icon + Title */}
				<div className='mb-3 flex items-center gap-2.5'>
					<div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink-500/10'>
						<Cookie className='h-4 w-4 text-pink-500' />
					</div>
					<h3 className={`${geistMonoFont.className} text-foreground text-sm font-semibold`}>Cookies</h3>
				</div>

				{/* Description */}
				<p className='text-muted-foreground mb-4 text-xs leading-relaxed'>
					We use essential cookies to keep you signed in and store your preferences. No tracking or advertising cookies.
				</p>

				{/* Actions */}
				<div className='flex items-center justify-between'>
					<Link
						href='/privacy#cookies'
						className='text-muted-foreground hover:text-foreground text-xs underline underline-offset-2 transition-colors'
					>
						Privacy Policy
					</Link>
					<Button
						onClick={handleAccept}
						size='sm'
						className='bg-pink-600 text-white hover:bg-pink-500'
					>
						Got it
					</Button>
				</div>
			</div>
		</div>
	);
}
